import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';

const TeamSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, threshold: 0.2 });
  const [hoveredMember, setHoveredMember] = useState(null);

  // Team members data
  const teamMembers = [
    {
      id: 1,
      name: "Fayia Davis",
      position: "Chief Executive Officer",
      image: "/assets/images/Fayia.png",
      bio: "Visionary leader with 15+ years in digital transformation and enterprise solutions.",
      expertise: ["Strategic Planning", "Digital Transformation", "Team Leadership"],
      linkedin: "#",
      email: "Fayia.davis@daraja.com"
    },
    {
      id: 2,
      name: "Cyrus Kipsang",
      position: "Chief Technology Officer",
      image: "/assets/images/cyrus.png",
      bio: "Technology architect specializing in cloud infrastructure and emerging technologies.",
      expertise: ["Cloud Architecture", "AI/ML", "System Design"],
      linkedin: "#",
      email: "Cyrus.kipsang@daraja.com"
    },
    {
      id: 3,
      name: "Brian kipkemoi",
      position: "Head of Design",
      image: "/assets/images/Brian.png",
      bio: "Creative director passionate about user-centered design and digital experiences.",
      expertise: ["UX/UI Design", "Design Systems", "User Research"],
      linkedin: "#",
      email: "emily.watson@daraja.labs"
    },
   /*
{
  id: 4,
  name: "David Kim",
  position: "Lead Developer",
  image: "/assets/images/David.png",
  bio: "Full-stack developer with expertise in modern web technologies and scalable systems.",
  expertise: ["React/Node.js", "DevOps", "Database Design"],
  linkedin: "#",
  email: "david.kim@daraja.labs"
},
{
  id: 5,
  name: "Lisa Thompson",
  position: "Cybersecurity Specialist",
  image: "/assets/images/lisa.png",
  bio: "Security expert focused on protecting digital assets and ensuring compliance.",
  expertise: ["Security Audits", "Compliance", "Risk Assessment"],
  linkedin: "#",
  email: "lisa.thompson@daraja.labs"
},
{
  id: 6,
  name: "James Wilson",
  position: "Data Analytics Lead",
  image: "/assets/images/james.png",
  bio: "Data scientist transforming complex data into actionable business insights.",
  expertise: ["Data Science", "Machine Learning", "Business Intelligence"],
  linkedin: "#",
  email: "james.wilson@daraja.labs"
}
*/

  ];

  return (
    <section ref={ref} className="py-20 lg:py-32 bg-muted/30 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-accent/20 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-48 h-48 bg-accent/10 rounded-full blur-2xl" />
      </div>
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          className="text-center mb-16 lg:mb-20"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-6">
            Meet Our
            <span className="text-accent block lg:inline lg:ml-4">Expert Team</span>
          </h2>
          <p className="text-lg md:text-xl text-text-secondary max-w-3xl mx-auto leading-relaxed">
            Our diverse team of technology experts, designers, and strategists work together to deliver exceptional results for our clients.
          </p>
        </motion.div>

        {/* Team Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12">
          {teamMembers?.map((member, index) => (
            <motion.div
              key={member?.id}
              className="group relative"
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              onMouseEnter={() => setHoveredMember(member?.id)}
              onMouseLeave={() => setHoveredMember(null)}
            >
              <div className="bg-card rounded-2xl p-6 lg:p-8 border border-border hover:border-accent/50 transition-all duration-300 hover:shadow-xl hover:shadow-accent/10">
                {/* Profile Image */}
                <div className="relative mb-6">
                  <div className="w-24 h-24 lg:w-32 lg:h-32 mx-auto rounded-full overflow-hidden border-4 border-accent/20 group-hover:border-accent/50 transition-all duration-300">
                    <Image
                      src={member?.image}
                      alt={member?.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                  </div>
                  
                  {/* Hover Overlay */}
                  <motion.div
                    className="absolute inset-0 bg-accent/90 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300"
                    initial={false}
                    animate={{
                      scale: hoveredMember === member?.id ? 1 : 0.8,
                      opacity: hoveredMember === member?.id ? 1 : 0
                    }}
                  >
                    <div className="flex space-x-3">
                      <a
                        href={member?.linkedin}
                        className="w-8 h-8 bg-accent-foreground rounded-full flex items-center justify-center hover:scale-110 transition-transform"
                      >
                        <Icon name="Linkedin" size={16} className="text-accent" />
                      </a>
                      <a
                        href={`mailto:${member?.email}`}
                        className="w-8 h-8 bg-accent-foreground rounded-full flex items-center justify-center hover:scale-110 transition-transform"
                      >
                        <Icon name="Mail" size={16} className="text-accent" />
                      </a>
                    </div>
                  </motion.div>
                </div>

                {/* Member Info */}
                <div className="text-center">
                  <h3 className="text-xl lg:text-2xl font-semibold text-card-foreground mb-2">
                    {member?.name}
                  </h3>
                  <p className="text-accent font-medium mb-4">{member?.position}</p>
                  <p className="text-muted-foreground text-sm lg:text-base leading-relaxed mb-6">
                    {member?.bio}
                  </p>

                  {/* Expertise Tags */}
                  <div className="flex flex-wrap justify-center gap-2">
                    {member?.expertise?.map((skill, skillIndex) => (
                      <span
                        key={skillIndex}
                        className="px-3 py-1 bg-accent/10 text-accent text-xs lg:text-sm rounded-full border border-accent/20"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Team Stats */}
        <motion.div
          className="grid grid-cols-2 lg:grid-cols-4 gap-8 mt-20 lg:mt-24"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.8 }}
        >
          <div className="text-center">
            <div className="text-3xl lg:text-4xl font-bold text-accent mb-2">25+</div>
            <div className="text-muted-foreground">Team Members</div>
          </div>
          <div className="text-center">
            <div className="text-3xl lg:text-4xl font-bold text-accent mb-2">50+</div>
            <div className="text-muted-foreground">Projects Delivered</div>
          </div>
          <div className="text-center">
            <div className="text-3xl lg:text-4xl font-bold text-accent mb-2">15+</div>
            <div className="text-muted-foreground">Countries Served</div>
          </div>
          <div className="text-center">
            <div className="text-3xl lg:text-4xl font-bold text-accent mb-2">99%</div>
            <div className="text-muted-foreground">Client Satisfaction</div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default TeamSection;